import { useRef, useState, useEffect, useCallback, useMemo } from 'react'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { TooltipProvider } from '@/components/ui/tooltip'
import { SidebarAssignmentAvatar, type BidMemberInfo } from '@/components/ui/owner-assignment'
import { Plus } from 'lucide-react'
import { toast } from 'sonner'
import type { OutlineSection } from '@/lib/api'
import { useDraftingStore } from '@/stores/drafting-store'
import { useSectionAssignmentsStore, type SectionAssignment } from '@/stores/section-assignments-store'
import { useSectionFlagsStore, mergeFlagsBySection, type MergedSectionFlag } from '@/stores/section-flags-store'
import { useAuthStore } from '@/stores/auth-store'
import { cn } from '@/lib/utils'
import { collaborationStatusConfig, type CollaborationStatusKey } from '@/lib/section-status'
import { SectionFlagMarker } from '@/components/drafting/SectionFlagMarker'

// ===================
// Types
// ===================

interface OutlineNavigatorProps {
  sections: OutlineSection[]
  selectedSectionKey: string | null
  onSectionSelect: (sectionKey: string) => void
  artifactId: string
  onRenameSection: (sectionKey: string, title: string) => void
  bidId: string | undefined
  bidMembers: BidMemberInfo[]
  isOwner: boolean
  isFinalized: boolean
  onAssignSection: (sectionKey: string, userId: string) => void
  onUnassignSection: (sectionKey: string) => void
}

// ===================
// Section Item
// ===================

interface SectionItemProps {
  section: OutlineSection
  index: number
  isSelected: boolean
  onSelect: () => void
  onRename: (sectionKey: string, title: string) => void
  assignment: SectionAssignment | undefined
  currentUserId: string | undefined
  bidMembers: BidMemberInfo[]
  canAssign: boolean
  isFinalized: boolean
  onAssign: (userId: string) => void
  onUnassign: () => void
  flag: MergedSectionFlag | undefined
  autoEdit?: boolean
  onAutoEditHandled?: () => void
}

function SectionItem({ section, index, isSelected, onSelect, onRename, assignment, currentUserId, bidMembers, canAssign, isFinalized, onAssign, onUnassign, flag, autoEdit, onAutoEditHandled }: SectionItemProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(section.title)
  const inputRef = useRef<HTMLInputElement>(null)

  // Collaboration status from assignment (unassigned = not_started)
  const statusKey: CollaborationStatusKey = assignment?.collaborationStatus ?? 'not_started'
  const statusInfo = collaborationStatusConfig[statusKey] ?? collaborationStatusConfig['not_started']

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  // Auto-enter edit mode when requested
  useEffect(() => {
    if (autoEdit && !isEditing) {
      setEditValue(section.title)
      setIsEditing(true)
      onAutoEditHandled?.()
    }
  }, [autoEdit, isEditing, section.title, onAutoEditHandled])

  const handleEditStart = () => {
    setEditValue(section.title)
    setIsEditing(true)
  }

  const handleEditSave = () => {
    if (editValue.trim() && editValue.trim() !== section.title) {
      onRename(section.sectionKey, editValue.trim())
    }
    setIsEditing(false)
  }

  const handleEditCancel = () => {
    setEditValue(section.title)
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleEditSave()
    } else if (e.key === 'Escape') {
      handleEditCancel()
    }
  }

  return (
    <div
      onClick={onSelect}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onSelect()
        }
      }}
      className={cn(
        'w-full text-left pl-2.5 pr-2 py-2 flex items-center gap-2 transition-colors group cursor-pointer border-l-2 border-l-transparent',
        'hover:bg-foreground/5',
        isSelected && 'bg-foreground/10 !border-l-[hsl(var(--brand))]',
      )}
    >
      {/* Assignment Avatar */}
      <SidebarAssignmentAvatar
        assignment={assignment}
        currentUserId={currentUserId}
        bidMembers={bidMembers}
        canAssign={canAssign}
        isFinalized={isFinalized}
        onAssign={onAssign}
        onUnassign={onUnassign}
        itemLabel="section"
      />

      {/* Content */}
      <div className="flex-1 min-w-0">
        {isEditing ? (
          <Input
            ref={inputRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleEditSave}
            onClick={(e) => e.stopPropagation()}
            className="h-6 text-[13px] px-1 py-0"
          />
        ) : (
          <div className="flex items-center gap-2">
            <p
              className={cn('text-[13px] truncate flex-1 min-w-0 transition-colors group-hover:text-[hsl(var(--brand))]', isSelected && 'font-medium')}
              onDoubleClick={(e) => {
                e.stopPropagation()
                // A locked outline (finalised, in review, view-only) can't be renamed.
                if (!isFinalized) handleEditStart()
              }}
            >
              <span className="text-muted-foreground tabular-nums">{index + 1}.</span> {section.title}
            </p>
            {flag && <SectionFlagMarker sourceLabel={flag.sourceLabel} />}
            <div
              className={cn('h-2 w-2 rounded-full shrink-0', statusInfo.dotColor)}
              title={statusInfo.label}
              aria-label={statusInfo.label}
            />
          </div>
        )}
      </div>
    </div>
  )
}

// ===================
// Quick Add Section
// ===================

interface QuickAddSectionProps {
  onAdd: () => void
}

function QuickAddSection({ onAdd }: QuickAddSectionProps) {
  return (
    <button
      onClick={onAdd}
      className="w-full px-3 py-2 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
    >
      <Plus className="h-4 w-4" />
      <span>Add Section</span>
    </button>
  )
}

// ===================
// Main Component
// ===================

export function OutlineNavigator({
  sections,
  selectedSectionKey,
  onSectionSelect,
  artifactId,
  onRenameSection,
  bidId,
  bidMembers,
  isOwner,
  isFinalized,
  onAssignSection,
  onUnassignSection,
}: OutlineNavigatorProps) {
  const addSectionAndSave = useDraftingStore((s) => s.addSectionAndSave)
  const listRef = useRef<HTMLDivElement>(null)
  const currentUserId = useAuthStore((s) => s.user?.id)
  const { assignments, fetchAssignments } = useSectionAssignmentsStore()
  const artifactFlags = useSectionFlagsStore((s) => s.flagsByArtifact[artifactId])
  const flagsBySection = useMemo(() => mergeFlagsBySection(artifactFlags ?? []), [artifactFlags])
  const [autoEditKey, setAutoEditKey] = useState<string | null>(null)

  // Fetch assignments on mount
  useEffect(() => {
    if (artifactId && bidId) {
      void fetchAssignments(artifactId)
    }
  }, [artifactId, bidId, fetchAssignments])

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!selectedSectionKey || sections.length === 0) return

      // Don't hijack arrow keys while the user is typing — the draft editor,
      // chat input, rename field, etc. need their native cursor behavior.
      const target = e.target as HTMLElement | null
      if (target) {
        const tag = target.tagName
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable) {
          return
        }
      }

      const currentIndex = sections.findIndex((s) => s.sectionKey === selectedSectionKey)
      if (currentIndex === -1) return

      if (e.key === 'ArrowUp' && currentIndex > 0) {
        e.preventDefault()
        const prevSection = sections[currentIndex - 1]
        if (prevSection) {
          onSectionSelect(prevSection.sectionKey)
        }
      } else if (e.key === 'ArrowDown' && currentIndex < sections.length - 1) {
        e.preventDefault()
        const nextSection = sections[currentIndex + 1]
        if (nextSection) {
          onSectionSelect(nextSection.sectionKey)
        }
      }
    },
    [sections, selectedSectionKey, onSectionSelect]
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  // Handle quick add — creates section with placeholder and enters rename mode.
  // The store adds optimistically and rolls the add back if the write fails, so
  // a failed add leaves no phantom section behind and the editor stays usable.
  const handleQuickAdd = async () => {
    if (!artifactId) return

    const sectionKey = `section-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`
    const title = 'Untitled Section'
    // Assumes the server echoes the client-minted sectionKey back untouched —
    // if it ever re-keys sections on save, this focus target goes stale.
    setAutoEditKey(sectionKey)

    const saved = await addSectionAndSave(artifactId, { sectionKey, title })
    if (!saved) {
      setAutoEditKey(null)
      toast.error("Couldn't add the section — try again.")
    }
  }

  return (
    <TooltipProvider delayDuration={300}>
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-3 pt-2">
        <div className="flex items-center justify-between h-8">
          <h3 className="text-sm font-medium text-muted-foreground">
            Sections
          </h3>
          <Badge variant="secondary" className="text-xs">
            {sections.length}
          </Badge>
        </div>
      </div>

      {/* Section List */}
      <ScrollArea className="flex-1">
        <div ref={listRef} className="py-1">
          {sections.map((section, index) => {
            const assignment = assignments.find((a) => a.sectionId === section.sectionKey)
            return (
              <SectionItem
                key={section.sectionKey}
                section={section}
                index={index}
                isSelected={section.sectionKey === selectedSectionKey}
                onSelect={() => onSectionSelect(section.sectionKey)}
                onRename={onRenameSection}
                assignment={assignment}
                currentUserId={currentUserId}
                bidMembers={bidMembers}
                canAssign={isOwner}
                isFinalized={isFinalized}
                onAssign={(userId) => onAssignSection(section.sectionKey, userId)}
                onUnassign={() => onUnassignSection(section.sectionKey)}
                flag={flagsBySection.get(section.sectionKey)}
                autoEdit={autoEditKey === section.sectionKey}
                onAutoEditHandled={() => setAutoEditKey(null)}
              />
            )
          })}
        </div>
      </ScrollArea>

      {/* Quick Add — not on a locked outline (finalised, in review, view-only) */}
      {!isFinalized && (
        <div className="pt-1">
          <QuickAddSection onAdd={() => void handleQuickAdd()} />
        </div>
      )}
    </div>
    </TooltipProvider>
  )
}
